import { useEffect, useRef, useState } from 'react';

/**
 * Whether an element's text is actually being clipped by `truncate`.
 *
 * CSS can hide overflow but cannot tell you that it did, so a tooltip meant to
 * recover cut-off text has no way to know whether there is any. Attaching one
 * unconditionally means it usually repeats what is already on screen.
 *
 * Measured rather than guessed: `scrollWidth` is the text's full width,
 * `clientWidth` the visible box, so they differ exactly when an ellipsis is
 * showing. Re-measured on resize *and* whenever `watch` changes, because the
 * two ways this flips are the panel being resized and the text itself being
 * replaced by a poll.
 *
 * The element must carry `truncate` (or its own `white-space: nowrap` and
 * `overflow: hidden`); this only reports, it does not clip.
 */
export function useIsTruncated<T extends HTMLElement>(watch?: unknown) {
  const ref = useRef<T>(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => setTruncated(element.scrollWidth > element.clientWidth);
    measure();

    // ResizeObserver rather than a window listener: the panel is a fixed-width
    // slide-over, so the width that matters changes when its content does, not
    // when the window does.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [watch]);

  return [ref, truncated] as const;
}
